/**
 * Home — the authenticated landing page (PEL-823).
 *
 * What the user sees after logging in:
 *   - Greeting with their first name
 *   - Wallet + PeliPOINTS chips (tappable)
 *   - Quick Actions grid: Photos / Letter / Card / Postcard
 *   - Active Orders (last 3)
 *   - Drafts (last 3) with resume + delete
 *   - What's New announcements (expand-in-place)
 *
 * Brand-new users (zero contacts) still get redirected to
 *   /onboarding/add-recipient — that's the PEL-822 onboarding gate.
 */

import { Navigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  IoWalletOutline,
  IoStarOutline,
  IoAddOutline,
} from 'react-icons/io5';

import { useAuth } from '@/context/AuthContext';
import { useContactsList } from '@/hooks/useContacts';
import { useOrdersList } from '@/hooks/useOrders';
import { useDraftsList, useDeleteDraft } from '@/hooks/useDrafts';
import { useAnnouncementsList } from '@/hooks/useAnnouncements';
import { useWalletBalance } from '@/hooks/useWallet';
import { usePeliPointsBalance } from '@/hooks/usePelipoints';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { SectionCard } from '@/components/domain/SectionCard';
import { QuickActionGrid } from '@/components/domain/QuickActionGrid';
import { MoneyChip } from '@/components/domain/MoneyChip';
import { OrderRow } from '@/components/domain/OrderRow';
import { DraftRow } from '@/components/domain/DraftRow';
import { AnnouncementRow } from '@/components/domain/AnnouncementRow';

function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

function formatPoints(points: number): string {
  return points.toLocaleString('en-US');
}

export function HomePage() {
  const { user } = useAuth();

  const contactsQuery = useContactsList();
  const ordersQuery = useOrdersList(1);
  const draftsQuery = useDraftsList();
  const announcementsQuery = useAnnouncementsList();
  const walletQuery = useWalletBalance();
  const pointsQuery = usePeliPointsBalance();

  const deleteDraft = useDeleteDraft();

  // Onboarding gate: wait for contacts to load, then redirect if zero.
  if (contactsQuery.isLoading) {
    return (
      <div
        className="px-4 md:px-6 py-10 text-[14px] text-[var(--text-muted)]"
        aria-live="polite"
      >
        Loading…
      </div>
    );
  }

  if (contactsQuery.data && contactsQuery.data.length === 0) {
    return <Navigate to="/onboarding/add-recipient" replace />;
  }

  const firstName = user?.firstName?.trim() || 'there';
  const orders = ordersQuery.data?.orders ?? [];
  const drafts = draftsQuery.data ?? [];
  const announcements = announcementsQuery.data ?? [];

  const topOrders = orders.slice(0, 3);
  const topDrafts = drafts.slice(0, 3);

  return (
    <div className="px-4 md:px-6 py-6 pb-12 max-w-5xl mx-auto space-y-6">
      {/* Greeting + money chips */}
      <header className="space-y-3">
        <div>
          <p className="text-[13px] text-[var(--text-muted)]">Welcome back</p>
          <h1 className="text-[22px] md:text-[26px] font-bold text-[var(--text)]">
            Hi, {firstName}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MoneyChip
            to="/app/wallet"
            icon={IoWalletOutline}
            value={walletQuery.data ? formatUsd(walletQuery.data.balanceCents) : '—'}
            label="Wallet"
            loading={walletQuery.isLoading}
            tone="brand"
          />
          <MoneyChip
            to="/app/pelipoints"
            icon={IoStarOutline}
            value={
              pointsQuery.data ? `${formatPoints(pointsQuery.data.balance)} pts` : '—'
            }
            label="PeliPOINTS"
            loading={pointsQuery.isLoading}
            tone="cta"
          />
        </div>
      </header>

      {/* Quick Actions */}
      <section>
        <h2 className="sr-only">Quick actions</h2>
        <QuickActionGrid />
      </section>

      {/* Active Orders */}
      <SectionCard
        title="Active orders"
        subtitle="Your most recent mail"
        viewAllTo={orders.length > 0 ? '/app/orders' : undefined}
      >
        {ordersQuery.isLoading ? (
          <OrdersSkeleton />
        ) : ordersQuery.isError ? (
          <InlineError
            message="Couldn't load your orders."
            onRetry={() => ordersQuery.refetch()}
          />
        ) : topOrders.length === 0 ? (
          <EmptyState
            title="You haven't sent anything yet"
            body="Pick a product above to get started."
          />
        ) : (
          <div>
            {topOrders.map((order) => (
              <OrderRow key={order.id} order={order} />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Drafts */}
      <SectionCard
        title="Drafts"
        subtitle="Pick up where you left off"
        viewAllTo={drafts.length > 0 ? '/app/drafts' : undefined}
      >
        {draftsQuery.isLoading ? (
          <DraftsSkeleton />
        ) : draftsQuery.isError ? (
          <InlineError
            message="Couldn't load your drafts."
            onRetry={() => draftsQuery.refetch()}
          />
        ) : topDrafts.length === 0 ? (
          <EmptyState
            title="No drafts in progress"
            body="Anything you start but don't finish will show up here."
          />
        ) : (
          <div>
            {topDrafts.map((draft) => (
              <DraftRow
                key={draft.id}
                draft={draft}
                deleting={deleteDraft.isPending}
                onDelete={(d) => {
                  deleteDraft.mutate(d.id, {
                    onSuccess: () => toast.success('Draft deleted'),
                    onError: () => toast.error('Could not delete that draft'),
                  });
                }}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Announcements — render only if there's at least one to show, otherwise hide. */}
      {announcementsQuery.isSuccess && announcements.length > 0 ? (
        <SectionCard title="What's new" subtitle="Updates from Pelipost">
          <div>
            {announcements.map((a) => (
              <AnnouncementRow key={a.id} announcement={a} />
            ))}
          </div>
        </SectionCard>
      ) : null}

      {/* Add-recipient nudge — always visible on Home, since multiple people is common */}
      <section className="pt-2">
        <Card className="p-4 md:p-5 flex items-center gap-3 bg-[var(--brand-light)] border-none">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[14px] text-[var(--brand)]">
              Writing to more than one person?
            </p>
            <p className="text-[12px] text-[var(--brand)] opacity-80">
              Add another recipient any time — each one keeps its own facility rules.
            </p>
          </div>
          <Button asChild variant="outline" size="sm" className="shrink-0">
            <Link to="/app/recipients/new">
              <IoAddOutline size={18} />
              <span>Add</span>
            </Link>
          </Button>
        </Card>
      </section>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function OrdersSkeleton() {
  return (
    <div>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border)] last:border-b-0"
        >
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-48" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}

function DraftsSkeleton() {
  return (
    <div>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border)] last:border-b-0"
        >
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="px-4 py-8 text-center">
      <p className="font-semibold text-[14px] text-[var(--text)]">{title}</p>
      <p className="mt-1 text-[13px] text-[var(--text-muted)]">{body}</p>
    </div>
  );
}

function InlineError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="p-4">
      <Alert>
        <AlertDescription className="flex items-center justify-between gap-3">
          <span>{message}</span>
          <Button size="sm" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
